import { useState, useRef, useCallback, useEffect } from "react";
import {
  supabaseClient,
  dbLoadSCCOFull,
  dbSavePushSubscription,
  dbDeletePushSubscription,
} from "../db";
import { loadSession, clearSession, saveSession } from "../utils/sessionUtils";
import { getLocal, setLocal, getSession as getStorageSession, setSession as setStorageSession } from "../utils/storageUtils";
import { registerNativePush, unregisterNativePush, isNative } from "../utils/nativePush";
import { BiometricAuth, BiometryType } from "@aparajita/capacitor-biometric-auth";
import { loadBioCreds, clearBioCreds } from "../utils/bioStorage";

// -- Push utilities -----------------------------------------------------------
// Full Web Push with VAPID — works when app is fully closed.
// Requires: Supabase push_subscriptions table + send-push Edge Function.

const PUSH_PREF_KEY    = "sa_push_enabled";
const VAPID_PUBLIC_KEY = "BNNOSFpkFelep1Uz-VBgOd6TWD1GXpq0Mb_BIGLgW6oYkmSqu7mHqrqNKVW1K1T4AahZElm3cW0f5orSDSMkTJ0";

export function loadPushPref() { return getLocal(PUSH_PREF_KEY, false) === true; }
export function savePushPref(val) { setLocal(PUSH_PREF_KEY, val); }

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    // Unregister any stale SW registrations (e.g. Blob URL SWs from older app versions)
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const reg of registrations) {
      const swUrl = reg.active?.scriptURL || reg.installing?.scriptURL || reg.waiting?.scriptURL || "";
      if (swUrl && !swUrl.endsWith("/sw.js")) {
        console.log("[SW] Unregistering stale SW:", swUrl);
        await reg.unregister();
      }
    }
    const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    await navigator.serviceWorker.ready;
    return reg;
  } catch (e) {
    console.warn("SW registration failed:", e);
    return null;
  }
}

export function canNotify() {
  return typeof Notification !== "undefined" && Notification.permission === "granted";
}

export async function requestNotificationPermission() {
  if (!("Notification" in window)) return "denied";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  return await Notification.requestPermission();
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return new Uint8Array([...rawData].map(c => c.charCodeAt(0)));
}

export async function subscribeToPush(airline) {
  try {
    if (!("serviceWorker" in navigator)) return null;
    if (!("PushManager" in window)) return null;
    const reg = await navigator.serviceWorker.ready;
    const existingSub = await reg.pushManager.getSubscription();

    if (existingSub) {
      // Existing subscription is still valid — just ensure Supabase has the current endpoint.
      // Do NOT unsubscribe and recreate: generating a new APNS/FCM endpoint on every load
      // causes rate-limiting and drops the row from the DB if the subscribe call fails.
      await dbSavePushSubscription(airline, existingSub);
      return existingSub;
    }

    // No existing subscription — create one fresh.
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
    await dbSavePushSubscription(airline, sub);
    return sub;
  } catch (e) {
    console.error("[Push] subscribeToPush failed:", e.name, e.message);
    return null;
  }
}

export async function unsubscribeFromPush(airline) {
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await dbDeletePushSubscription(sub.endpoint);
      await sub.unsubscribe();
    }
  } catch (e) {
    console.warn("Push unsubscribe failed:", e);
  }
}

// -- Biometric re-auth --------------------------------------------------------

export async function biometricAvailable() {
  if (!isNative()) return false;
  try {
    const { isAvailable } = await BiometricAuth.checkBiometry();
    return isAvailable;
  } catch {
    return false;
  }
}

// "Touch ID" on Touch ID devices (iPhone Duo, iPhone SE, older iPads), else "Face ID".
export async function biometricLabel() {
  if (!isNative()) return "Face ID";
  try {
    const { biometryType } = await BiometricAuth.checkBiometry();
    return biometryType === BiometryType.touchId ? "Touch ID" : "Face ID";
  } catch {
    return "Face ID";
  }
}

export async function faceIdReauth() {
  if (!isNative()) return false;
  try {
    await BiometricAuth.authenticate({ reason: "Confirm it's you to stay logged in" });
    const { error } = await supabaseClient.auth.refreshSession();
    return !error;
  } catch {
    return false;
  }
}

// Full biometric sign-in: prompts Face ID, then exchanges the stored refresh
// token for a new Supabase session and reconstructs the app session object.
// Returns { session } on success, { error: "cancelled"|"session_expired" } on failure.
export async function bioLogin() {
  if (!isNative()) return null;
  const creds = await loadBioCreds();
  if (!creds?.airline) return null;

  try {
    await BiometricAuth.authenticate({ reason: "Sign in to SA Connections", iosFallbackTitle: "Use Password" });
  } catch {
    return { error: "cancelled" };
  }

  // Supabase refresh token lives in localStorage already — just refresh the session.
  // Don't clear bio creds on failure (network errors or expired token) — the user
  // can sign in with password once to get a fresh token; no re-enrollment needed.
  const { data, error } = await supabaseClient.auth.refreshSession();
  if (error || !data?.session) {
    return { error: "session_expired" };
  }

  const meta = data.user?.user_metadata ?? {};
  const session = creds.airline === "UA"
    ? { airline: "UA", isUA: true, scco: creds.scco || null, jwt: true, meta }
    : { airline: creds.airline, isUA: false, scco: null, partnerName: creds.partnerName || null, jwt: true, meta };

  saveSession(session);
  return { session };
}

// -- Cross-window session sync ------------------------------------------------
// Mission Control opens as a popup (?mc=1). Both windows share the same origin
// so BroadcastChannel lets them stay in sync: when either window signs out,
// the other follows immediately.

const IS_POPUP = new URLSearchParams(window.location.search).get("mc") === "1";
const authChannel = typeof BroadcastChannel !== "undefined"
  ? new BroadcastChannel("sa_auth")
  : null;

// -- Hook ---------------------------------------------------------------------

export function useAuth() {
  const [session,            setSession]            = useState(() => loadSession());
  const [activeSCCO,         setActiveSCCO]         = useState(() => {
    // On native, try sessionStorage first, then fall back to the persisted session
    // (sessionStorage is wiped on WKWebView restart but session is now in localStorage)
    const stored = getStorageSession("sa_lax_scco", null);
    if (stored) return stored;
    if (isNative()) return loadSession()?.scco ?? null;
    return null;
  });
  const [sccoList,           setSccoList]           = useState([]);
  const [pushEnabled,        setPushEnabled]        = useState(() => loadPushPref());
  const [pushPermission,     setPushPermission]     = useState(() =>
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [showInactivity,     setShowInactivity]     = useState(false);
  const [inactivitySecsLeft, setInactivitySecsLeft] = useState(300);

  const inactivityTimer    = useRef(null); // fires at 30min → show modal
  const countdownTimer     = useRef(null); // fires every 1s during modal
  const lastActivity       = useRef(Date.now());
  const sessionRef         = useRef(session); // tracks current session without stale-closure risk
  const showInactivityRef  = useRef(false);
  const signingOutRef      = useRef(false);  // prevents double-invoke from rapid SIGNED_OUT events

  // Derived session values
  const isUA        = session ? session.isUA      : false;
  const myAirline   = session ? session.airline   : null;
  const partnerName = session ? (session.partnerName || null) : null;
  const isAdmin     = !!(session?.meta?.is_admin);

  const doSignOut = useCallback(async () => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    clearInterval(countdownTimer.current);
    clearTimeout(inactivityTimer.current);
    await unregisterNativePush();
    clearSession();
    setSession(null);
    setActiveSCCO(null);
    setShowInactivity(false);
    authChannel?.postMessage({ type: "SIGNED_OUT" });
    await supabaseClient.auth.signOut();
  }, []);

  const resetInactivity = useCallback(() => {
    if (!session || !session.isUA) return;
    lastActivity.current = Date.now();
    // Dismiss warning if showing and restart full timer
    if (showInactivityRef.current) {
      clearInterval(countdownTimer.current);
      showInactivityRef.current = false;
      setShowInactivity(false);
      setInactivitySecsLeft(300);
    }
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      // 45 min elapsed — show warning, start 5-min countdown
      showInactivityRef.current = true;
      setShowInactivity(true);
      setInactivitySecsLeft(300);
      countdownTimer.current = setInterval(() => {
        setInactivitySecsLeft(s => {
          if (s <= 1) {
            clearInterval(countdownTimer.current);
            doSignOut();
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    }, 45 * 60 * 1000);
  }, [session, doSignOut]);

  // Auto-refresh push subscription on login
  useEffect(() => {
    if (!session) return;
    if (!pushEnabled) return;
    if (!canNotify()) return;
    subscribeToPush(isUA ? "UA" : myAirline);
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps -- runs once on login; pushEnabled/myAirline are stable at that point and we don't want repeated re-subscriptions

  // Native APNs registration (Capacitor iOS only) — no-ops on web
  useEffect(() => {
    if (!session) return;
    registerNativePush({ airline: isUA ? "UA" : myAirline });
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps -- same pattern as web push above

  // Start/restart inactivity tracking when session changes.
  // Disabled in popup mode — the popup follows the main window's session lifecycle.
  useEffect(() => {
    if (IS_POPUP) return;
    if (session && session.isUA) {
      resetInactivity();
    } else {
      clearTimeout(inactivityTimer.current);
      clearInterval(countdownTimer.current);
      setShowInactivity(false);
    }
    return () => {
      clearTimeout(inactivityTimer.current);
      clearInterval(countdownTimer.current);
    };
  }, [!!session]); // eslint-disable-line react-hooks/exhaustive-deps -- boolean coercion is intentional; resetInactivity must not be a dep or it creates a loop via showInactivity

  // Hard logout after 10 hours — main window only; popup follows via BroadcastChannel.
  useEffect(() => {
    if (IS_POPUP || !session) return;
    const t = setTimeout(() => { doSignOut(); }, 10 * 60 * 60 * 1000);
    return () => clearTimeout(t);
  }, [doSignOut, !!session]); // eslint-disable-line react-hooks/exhaustive-deps -- !!session coercion intentional; avoids timer reset on every token refresh

  // Keep sessionRef in sync so the onAuthStateChange handler below can read the
  // current session without a stale closure (the handler is registered once with [] deps).
  useEffect(() => { sessionRef.current = session; }, [session]);

  // Mirror Supabase's own session state into React — if the JWT refresh fails
  // (token expired, refresh token rotated out, network loss) Supabase fires
  // SIGNED_OUT but our React session state stays set, leaving the app looking
  // logged-in while all fetches silently fail. This keeps them in sync.
  //
  // Guard: only sign out if the React app currently has an active session.
  // If SIGNED_OUT fires while the user is on the login screen (session = null),
  // calling doSignOut() would invoke supabaseClient.auth.signOut() which clears
  // the freshly-established Supabase session and causes a sign-out loop.
  useEffect(() => {
    const { data: { subscription } } = supabaseClient.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT' && sessionRef.current !== null) doSignOut();
    });
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- doSignOut is stable (useCallback with [] deps)

  // BroadcastChannel: when the main window signs out, close the popup session
  // (and vice versa). loadSession() guard prevents a sign-out echo loop.
  useEffect(() => {
    if (!authChannel) return;
    const onMessage = (e) => {
      if (e.data?.type === "SIGNED_OUT" && loadSession()) doSignOut();
    };
    authChannel.addEventListener("message", onMessage);
    return () => authChannel.removeEventListener("message", onMessage);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- doSignOut is stable

  // Wall-clock guard: JS timers are suspended during OS sleep/screen lock.
  // On resume, if the full inactivity window has elapsed, sign out immediately
  // rather than showing the "still there?" modal for an already-expired session.
  // Disabled in popup mode — handled by the main window.
  useEffect(() => {
    if (IS_POPUP || !session || !session.isUA) return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastActivity.current >= 50 * 60 * 1000) {
        doSignOut();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [!!session, doSignOut]); // eslint-disable-line react-hooks/exhaustive-deps -- boolean coercion is intentional; lastActivity is a ref so doesn't need to be listed

  // Listen for user activity — resetInactivity checks showInactivityRef internally
  useEffect(() => {
    if (!session || !session.isUA) return;
    const events = ["mousedown", "keydown", "touchstart", "click"];
    const handler = () => resetInactivity();
    events.forEach(ev => window.addEventListener(ev, handler, { passive: true }));
    return () => events.forEach(ev => window.removeEventListener(ev, handler));
  }, [resetInactivity, !!session]); // eslint-disable-line react-hooks/exhaustive-deps -- !!session coercion intentional

  // Load SCCO roster when UA logs in
  useEffect(() => {
    if (session && session.isUA) {
      dbLoadSCCOFull().then(list => setSccoList(list));
    }
  }, [session]);

  // Persist activeSCCO — localStorage on native so it survives WKWebView restarts
  useEffect(() => {
    if (isNative()) setLocal("sa_lax_scco", activeSCCO);
    else setStorageSession("sa_lax_scco", activeSCCO);
  }, [activeSCCO]);

  return {
    session, setSession,
    activeSCCO, setActiveSCCO,
    sccoList, setSccoList,
    isUA, myAirline, partnerName, isAdmin,
    doSignOut,
    showInactivity, setShowInactivity,
    inactivitySecsLeft,
    resetInactivity,
    pushEnabled, setPushEnabled,
    pushPermission, setPushPermission,
  };
}
